import { useState } from "react";
import { CheckCircle2, AlertCircle, Building2, User, ExternalLink, Database, RefreshCw, Loader2 } from "lucide-react";
import ContactOpportunitiesBlock from "./ContactOpportunitiesBlock";

const CRMStatusBanner = ({ contactStatus, companyStatus, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);
  const hasContact = contactStatus !== null;
  const hasCompany = companyStatus !== null;
  const contactInSellsy = contactStatus?.source === 'sellsy';
  const companyInSellsy = companyStatus?.source === 'sellsy';

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusStyle = (exists, inSellsy) => {
    if (!exists) return 'bg-amber-50 text-amber-700 border border-amber-200';
    if (inSellsy) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    return 'bg-blue-50 text-blue-700 border border-blue-200';
  };

  return (
    <div className="space-y-2 mb-3" data-testid="crm-status-banner">
      {/* Refresh control: re-query Sellsy with the current (possibly edited) extractedData */}
      {onRefresh && (
        <div className="flex items-center justify-between -mb-1">
          <span className="text-[10px] uppercase tracking-wide font-medium text-slate-500">
            Statut Sellsy
          </span>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-1.5 py-0.5 rounded hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-slate-300"
            title="Re-vérifier dans Sellsy avec les valeurs corrigées"
            data-testid="crm-status-refresh-btn"
          >
            {refreshing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            {refreshing ? "Vérification..." : "Re-vérifier"}
          </button>
        </div>
      )}

      {/* Contact Status */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium ${getStatusStyle(hasContact, contactInSellsy)}`}
        data-testid="contact-status-banner"
      >
        {hasContact ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5" />
            <User className="w-3 h-3" />
            <span className="flex-1 truncate">{contactStatus.name}</span>
            {contactInSellsy && contactStatus.sellsy_id ? (
              <a
                href={`https://www.sellsy.com/peoples/${contactStatus.sellsy_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[10px] hover:underline focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded px-1"
                title="Ouvrir le contact dans Sellsy"
                data-testid="banner-sellsy-contact-link"
              >
                <ExternalLink className="w-3 h-3" /> Sellsy
              </a>
            ) : (
              <span className="flex items-center gap-1 text-[10px] opacity-75">
                <Database className="w-2.5 h-2.5" /> Local
              </span>
            )}
          </>
        ) : (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <User className="w-3 h-3" />
            <span>Nouveau contact</span>
          </>
        )}
      </div>

      {/* Company Status */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium ${getStatusStyle(hasCompany, companyInSellsy)}`}
        data-testid="company-status-banner"
      >
        {hasCompany ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5" />
            <Building2 className="w-3 h-3" />
            <span className="flex-1 truncate">{companyStatus.name}</span>
            {companyInSellsy && companyStatus.sellsy_id ? (
              <a
                href={`https://www.sellsy.com/companies/${companyStatus.sellsy_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[10px] hover:underline focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded px-1"
                title="Ouvrir la société dans Sellsy"
                data-testid="banner-sellsy-company-link"
              >
                <ExternalLink className="w-3 h-3" /> Sellsy
              </a>
            ) : (
              <span className="flex items-center gap-1 text-[10px] opacity-75">
                <Database className="w-2.5 h-2.5" /> Local
              </span>
            )}
          </>
        ) : (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <Building2 className="w-3 h-3" />
            <span>Nouvelle societe</span>
          </>
        )}
      </div>

      {/* Recent opportunities attached to the matched Sellsy contact */}
      {contactInSellsy && contactStatus?.sellsy_id && (
        <ContactOpportunitiesBlock
          contactSellsyId={contactStatus.sellsy_id}
          companySellsyId={companyInSellsy ? companyStatus?.sellsy_id : null}
        />
      )}
    </div>
  );
};

export default CRMStatusBanner;
